import apiClient from "@/libs/api";
import config from "@/config";
import { getSession } from "next-auth/react";
import {
  getPurchaseContext,
  storePurchaseContext,
  storeSessionForRedirect,
  trackFunnelEvent,
} from "@/libs/ga";
import {
  billingIntervalForPriceId,
  displayPriceForInterval,
  findPlanByPriceId,
} from "@/libs/stripePlans";
import {
  hideRedirectLoading,
  redirectTo,
  REDIRECT_MESSAGES,
  showRedirectLoading,
} from "@/libs/redirectLoading";

export interface StripeCheckoutParams {
  priceId: string;
  mode: "payment" | "subscription";
  quantity?: number;
  couponId?: string;
  credits?: number;
  purchaseTrigger?: "voluntary" | "forced";
  /** Wait for the session cookie after popup/magic-link auth before checkout. */
  waitForSession?: boolean;
  /** When true, skip the sitewide redirect overlay (button-level loading instead). */
  skipRedirectOverlay?: boolean;
}

async function waitForAuthenticatedSession(
  maxAttempts = 10,
  delayMs = 250
): Promise<boolean> {
  for (let i = 0; i < maxAttempts; i++) {
    const session = await getSession();
    if (session?.user) return true;
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
  return false;
}

/**
 * Create a Stripe Checkout session and redirect the browser to it.
 * Used after sign-in (Google popup or email magic-link return) and for
 * already-authenticated checkout clicks.
 */
export async function startStripeCheckout(
  params: StripeCheckoutParams
): Promise<boolean> {
  const {
    priceId,
    mode,
    quantity,
    couponId,
    credits,
    purchaseTrigger = "voluntary",
    waitForSession = false,
    skipRedirectOverlay = false,
  } = params;

  if (waitForSession) {
    const ready = await waitForAuthenticatedSession();
    if (!ready) return false;
  }

  if (!skipRedirectOverlay) {
    showRedirectLoading(REDIRECT_MESSAGES.checkout);
  }

  storeSessionForRedirect("stripe");

  const existingContext = getPurchaseContext(false) || {};
  const plan = findPlanByPriceId(priceId);
  const purchaseValue =
    plan != null
      ? displayPriceForInterval(plan, billingIntervalForPriceId(plan, priceId))
      : undefined;
  const purchaseContext = {
    ...existingContext,
    price_id: priceId,
    mode,
    credits,
    value: purchaseValue,
    quantity: mode === "subscription" ? 1 : quantity,
    has_coupon: !!couponId,
    purchase_trigger: existingContext.purchase_trigger || purchaseTrigger,
    package_type: mode === "subscription" ? "subscription" : "topup",
    resume_after_auth: false,
  };
  storePurchaseContext(purchaseContext);

  trackFunnelEvent("monetization", "checkout_started", purchaseContext);

  try {
    const { url }: { url: string } = await apiClient.post("/stripe/create-checkout", {
      priceId,
      successUrl: `${window.location.origin}${config.payments.successUrl}`,
      cancelUrl: `${window.location.origin}${config.payments.cancelUrl}`,
      mode,
      quantity: mode === "subscription" ? 1 : quantity,
      couponId: couponId || null,
    });

    trackFunnelEvent("monetization", "checkout_created", purchaseContext);
    if (skipRedirectOverlay) {
      window.location.href = url;
    } else {
      redirectTo(url, REDIRECT_MESSAGES.checkout);
    }
    return true;
  } catch (e: unknown) {
    if (!skipRedirectOverlay) {
      hideRedirectLoading();
    }
    const message = e instanceof Error ? e.message : "Unknown error";
    console.error("[stripeCheckout]", e);
    trackFunnelEvent("monetization", "checkout_error", {
      ...purchaseContext,
      error: message,
    });
    return false;
  }
}
